import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, generatePath, useSearchParams } from 'react-router-dom';
import {
  PageContainerWrapper,
  TablePlus,
  CommonButton,
  GlobalLoader,
  ModalWrapper,
  NoDataFound,
} from '../../../components';
import { Select, Tooltip } from 'antd';
import {
  Columns,
  getCustomerOutstandingDetailService,
  sendEmail,
  markInvoicePaidService,
} from '../../../services/index.service';
import { accessRoute } from '../../../routes/Reports';
import { backToPage, modalNotification } from '../../../utils';
import { checkValidPrice } from '../../../components/UIElements/Formatter';
import { useTranslation } from 'react-i18next';
import { accessRoute as commonAccessRoutes } from '../../../routes/CommonRoutes';

const PAYMENT_MODE_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'other', label: 'Other' },
];

function OutstandingPaymentsDetail() {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const navigate = useNavigate();
  const breadcrumb = Columns.outstandingPaymentsDetailBreadcrumb();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [state, setState] = useState({
    modal: null,
    selectedInvoice: null,
    paymentMode: 'cash',
    actionLoader: false,
  });

  const { modal, selectedInvoice, paymentMode, actionLoader } = state;

  const page = parseInt(searchParams.get('page') || 1, 10);
  const sizePerPage = parseInt(searchParams.get('pageSize') || 10, 10);
  const searchFilter = searchParams.get('search') || '';

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const queryParams = {
        limit: sizePerPage,
        offset: (page - 1) * sizePerPage,
        search: searchFilter,
      };
      const response = await getCustomerOutstandingDetailService(customerId, queryParams);
      if (response?.success) {
        setCustomer(response?.data?.customer);
        setInvoices(response?.data?.invoices || []);
        setTotalCount(response?.data?.count || 0);
      }
    } catch (error) {
      console.log('Error fetching detail:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customerId) {
      fetchDetail();
    }
  }, [customerId, page, sizePerPage, searchFilter]);

  const handleSendLink = async (row) => {
    try {
      const res = await sendEmail(row?.bookingId, { email: customer?.email });
      if (res?.success) {
        modalNotification({
          type: 'success',
          message: res?.message || t('text.report.outstandingPaymentLinkSuccess'),
        });
      }
    } catch (err) {
      modalNotification({
        type: 'error',
        message: err?.message || t('text.report.outstandingPaymentLinkError'),
      });
    }
  };

  const openMarkPaidModal = (row) => {
    setState({
      ...state,
      modal: 'mark-paid',
      selectedInvoice: row,
      paymentMode: 'cash',
    });
  };

  const closeModal = () => {
    setState({ ...state, modal: null, selectedInvoice: null });
  };

  const handleMarkPaid = async () => {
    if (!selectedInvoice) return;
    setState({ ...state, actionLoader: true });
    try {
      const res = await markInvoicePaidService({
        bookingId: selectedInvoice?.bookingId,
        paymentMode,
      });
      if (res?.success) {
        modalNotification({
          type: 'success',
          message: res?.message || t('text.report.markInvoicePaidSuccess'),
        });
        closeModal();
        fetchDetail();
      }
    } catch (err) {
      modalNotification({
        type: 'error',
        message: err?.message || t('text.report.markInvoicePaidError'),
      });
    } finally {
      setState((prev) => ({ ...prev, actionLoader: false }));
    }
  };

  const invoiceActionFormatter = ({ row }) => (
    <div className="d-flex gap-2 align-items-center">
      <Tooltip title={t('text.report.sendLink')}>
        {/* <CommonButton
          className="btn btn-icon btn-outline-primary btn-sm"
          onClick={() => handleSendLink(row)}
        >
          <em className="icon ni ni-mail" />
        </CommonButton> */}
        <CommonButton
          className="btn btn-primary btn-sm ms-1"
          onClick={() => handleSendLink(row)}
        >
          {t('text.report.sendLink') || 'Send Link'}
        </CommonButton>
      </Tooltip>
      {/* <Tooltip title={t('text.report.markAsPaid')}>
        <CommonButton
          className="btn btn-icon btn-primary btn-sm"
          onClick={() => openMarkPaidModal(row)}
        >
          <em className="icon ni ni-check-circle" />
        </CommonButton>
      </Tooltip> */}
      <Tooltip title={t('text.report.viewInvoice') || 'View Invoice'}>
        {/* <CommonButton
          className="btn btn-icon btn-primary btn-sm"
          onClick={() => {
            const path = generatePath(commonAccessRoutes.INVOICE.path, {
              viewInvoice: 'invoice',
              routePath: 'outstanding-payments',
              id: row?.bookingId,
            });
            window.open(path, '_blank');
          }}//icon ni ni-eye
        >
          <em className="icon ni ni-eye" />
        </CommonButton> */}
        <CommonButton
          className="btn btn-primary btn-sm ms-1"
          onClick={() => {
            const path = generatePath(commonAccessRoutes.INVOICE.path, {
              viewInvoice: 'invoice',
              routePath: 'outstanding-payments',
              id: row?.bookingId,
            });
            window.open(path, '_blank');
          }}
        >
          {t('text.report.viewInvoice') || 'View Invoice'}
        </CommonButton>
      </Tooltip>
    </div>
  );

  const noOfPage = Math.ceil(totalCount / sizePerPage);

  const onFilterChange = (filterObj) => {
    if (filterObj && typeof filterObj === 'object') {
      Object.keys(filterObj).forEach((key) => {
        if (filterObj[key]) {
          searchParams.set(key, filterObj[key]);
        } else {
          searchParams.delete(key);
        }
      });
      searchParams.set('page', 1);
      setSearchParams(searchParams);
    }
  };

  const onFilterResetChange = () => {
    searchParams.delete('search');
    searchParams.set('page', 1);
    setSearchParams(searchParams);
  };

  const invoiceColumns = Columns.outstandingPaymentsInvoiceTableColumn({
    page,
    sizePerPage,
    Action: invoiceActionFormatter,
    onFilterChange,
    filter: { search: searchFilter }
  });

  const customerName = customer
    ? `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim()
    : '';

  const phoneDisplay = customer?.phoneNumber
    ? `${customer?.countryPhoneCode ? `(+${String(customer.countryPhoneCode).replace('+', '')}) ` : ''}${customer.phoneNumber}`
    : '-';

  return (
    <>
      <PageContainerWrapper
        heading={t('text.report.customerOutstandingDetailHeading')}
        breadcrumb={breadcrumb}
        btnArray={['extraButton']}
        extraBtnText="Back"
        extraBtnClass="btn btn-outline-light bg-white"
        onExtraButtonHandleShow={() =>
          backToPage(navigate, accessRoute.OUTSTANDING_PAYMENTS.path)
        }
      >
        {(!customer && loading) ? (
          <GlobalLoader />
        ) : !customer && !loading ? (
          <NoDataFound
            source="/nodata-found.svg"
            text={t('text.report.noCustomerDetailsFound')}
          />
        ) : (
          <>
            {/* Customer Info Card */}
            {customer && (
              <div className="nk-block">
                <div className="card card-bordered mb-0">
                  <div className="card-inner">
                    <div className="row g-3">
                      <div className="col-md-6 col-xl-3">
                        <div className="user-card">
                          <div>
                            <span className="sub-text">{t('text.report.customerNameInfo')}</span>
                            <span className="lead-text">{customerName || '-'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6 col-xl-3">
                        <div>
                          <span className="sub-text">{t('text.report.eMail')}</span>
                          <span className="lead-text">{customer?.email || '-'}</span>
                        </div>
                      </div>
                      <div className="col-md-6 col-xl-3">
                        <div>
                          <span className="sub-text">{t('text.report.phone')}</span>
                          <span className="lead-text">{phoneDisplay}</span>
                        </div>
                      </div>
                      <div className="col-md-6 col-xl-3">
                        <div>
                          <span className="sub-text">{t('text.report.totalOutstanding')}</span>
                          <span className="lead-text text-danger fw-bold">
                            {checkValidPrice(customer?.totalOutstanding)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Outstanding Invoices Table */}
            <div className="nk-block">
                <TablePlus
                  hasLimit
                  noOfPage={noOfPage}
                  sizePerPage={sizePerPage}
                  page={page}
                  count={totalCount}
                  tableData={invoices}
                  tableColumns={invoiceColumns}
                  tableLoader={loading}
                  onFilterResetChange={onFilterResetChange}
                  filter={{ search: searchFilter }}
                  pagination={true}
                  statusAction={false}
                  showAction={false}
                  selectRow={true}
                  header={true}
                  searchField={false}
                  isCard={true}
                />
            </div>
          </>
        )}
      </PageContainerWrapper>

      {/* Mark as Paid Modal */}
      {/* <ModalWrapper
        heading={t('text.report.markInvoicePaidModalHeading')}
        show={modal === 'mark-paid'}
        onCancel={closeModal}
        content={
          selectedInvoice && (
            <div>
              <div className="mb-3">
                <div className="row g-3">
                  <div className="col-sm-6">
                    <span className="sub-text">{t('text.report.bookingNo')}</span>
                    <span className="lead-text">
                      {selectedInvoice?.bookingNo || '-'}
                    </span>
                  </div>
                  <div className="col-sm-6">
                    <span className="sub-text">{t('text.report.dueAmount')}</span>
                    <span className="lead-text text-danger fw-bold">
                      {checkValidPrice(selectedInvoice?.remainingAmount)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">{t('text.report.selectPaymentMode')}</label>
                <Select
                  style={{ width: '100%' }}
                  value={paymentMode}
                  onChange={(val) => setState({ ...state, paymentMode: val })}
                  options={PAYMENT_MODE_OPTIONS}
                  size="large"
                />
              </div>
              <div className="text-end">
                <CommonButton
                  className="btn btn-outline-secondary me-2"
                  onClick={closeModal}
                >
                  {t('text.report.cancelMarkPaid')}
                </CommonButton>
                <CommonButton
                  className="btn btn-primary"
                  onClick={handleMarkPaid}
                  disabled={actionLoader}
                >
                  {actionLoader ? 'Processing...' : t('text.report.submitMarkPaid')}
                </CommonButton>
              </div>
            </div>
          )
        }
      /> */}
    </>
  );
}

export default OutstandingPaymentsDetail;
